import { useState, useEffect, useCallback } from 'react';
import { DataTable } from '../components/common/DataTable';
import { PageHeader } from '../components/common/PageHeader';
import { SearchFilter } from '../components/common/SearchFilter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MoreHorizontal, Eye, Edit, Trash2, Plus, Loader2, Send, CheckCircle, XCircle, Clock } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const WORK_TYPES = ['Billable', 'Non-Billable', 'Internal', 'Training'];
const ACTIVITY_CATEGORIES = ['Development', 'Testing', 'Support', 'Meeting', 'Travel', 'Admin', 'Other'];
const WORK_MODES = ['Office', 'Remote', 'Client Site'];

export default function TimesheetsPage() {
  const { user } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [creating, setCreating] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ total_entries: 0, total_hours: 0, overtime_hours: 0, billable_hours: 0 });
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    project: '',
    task_id: '',
    check_in_time: '09:00',
    check_out_time: '18:00',
    break_minutes: 60,
    work_type: 'Billable',
    activity_category: 'Development',
    task_description: '',
    work_mode: 'Office',
    location: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });
      if (status !== 'all') params.append('status', status);

      const response = await axios.get(`${API_URL}/api/timesheets?${params}`);
      setTimesheets(response.data.timesheets || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        totalPages: response.data.total_pages || 1,
      }));
    } catch (error) {
      toast.error('Failed to load timesheets');
      setTimesheets([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, status]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/timesheets/stats/summary`);
      setStats(response.data);
    } catch { /* ignore */ }
  };

  const fetchTasks = async () => {
    try {
      // Fetch all tasks (not just In Progress) that are not completed or cancelled
      const response = await axios.get(`${API_URL}/api/tasks?limit=100`);
      // Filter out completed and cancelled tasks
      const activeTasks = (response.data.tasks || []).filter(t => {
        const isActive = !['Completed', 'Cancelled'].includes(t.status);
        // Show all active tasks - the employee can log time on any task assigned to them
        // assigned_to is an array of objects with employee_id field
        if (!user?.id) return isActive;
        const isAssignedToUser = !t.assigned_to || t.assigned_to.length === 0 || 
          t.assigned_to.some(a => a.employee_id === user.id);
        return isActive && isAssignedToUser;
      });
      setTasks(activeTasks);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchTimesheets();
    fetchStats();
    fetchTasks();
  }, [fetchTimesheets]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newEntry.task_description) {
      toast.error('Task description is required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/timesheets?employee_id=${user?.id}`, newEntry);
      toast.success('Timesheet entry created');
      setShowCreateModal(false);
      setNewEntry({
        date: new Date().toISOString().split('T')[0],
        project: '',
        task_id: '',
        check_in_time: '09:00',
        check_out_time: '18:00',
        break_minutes: 60,
        work_type: 'Billable',
        activity_category: 'Development',
        task_description: '',
        work_mode: 'Office',
        location: '',
      });
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create timesheet');
    } finally {
      setCreating(false);
    }
  };

  const handleSubmit = async (ts) => {
    try {
      await axios.post(`${API_URL}/api/timesheets/${ts.id}/submit`);
      toast.success('Timesheet submitted for approval');
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleApprove = async (ts) => {
    try {
      await axios.post(`${API_URL}/api/timesheets/${ts.id}/approve?approved_by=${user?.id}`);
      toast.success('Timesheet approved');
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (ts) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await axios.post(`${API_URL}/api/timesheets/${ts.id}/reject?reason=${encodeURIComponent(reason)}&rejected_by=${user?.id}`);
      toast.success('Timesheet rejected');
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleDelete = async (ts) => {
    if (!window.confirm('Delete this timesheet entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/timesheets/${ts.id}`);
      toast.success('Timesheet deleted');
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getStatusColor = (s) => {
    const colors = {
      Draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      Submitted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      Approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      Rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[s] || colors.Draft;
  };

  const columns = [
    {
      key: 'employee',
      header: 'Employee',
      render: (row) => (
        <div>
          <p className="font-medium text-white">{row.employee_name}</p>
          <p className="text-xs text-slate-500">{row.date} ({row.day})</p>
        </div>
      ),
    },
    {
      key: 'project',
      header: 'Project/Task',
      render: (row) => (
        <div className="max-w-xs">
          <p className="text-slate-300 truncate">{row.project || row.task_title || '-'}</p>
          <p className="text-xs text-slate-500">{row.activity_category}</p>
        </div>
      ),
    },
    {
      key: 'time',
      header: 'Time',
      render: (row) => (
        <div className="text-sm">
          <p className="text-slate-300 font-mono">{row.check_in_time} - {row.check_out_time}</p>
          <p className="text-xs text-slate-500">Break: {row.break_minutes}m</p>
        </div>
      ),
    },
    {
      key: 'hours',
      header: 'Hours',
      render: (row) => (
        <div className="text-center">
          <p className="text-white font-bold tabular-nums">{row.net_hours}h</p>
          {row.overtime_hours > 0 && (
            <p className="text-xs text-orange-400">+{row.overtime_hours}h OT</p>
          )}
        </div>
      ),
    },
    {
      key: 'work_type',
      header: 'Type',
      render: (row) => (
        <span className={`text-xs px-2 py-0.5 rounded ${row.work_type === 'Billable' ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
          {row.work_type}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(row.submission_status)}`}>
          {row.submission_status}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`ts-actions-${row.id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
            <DropdownMenuItem 
              onClick={() => { setSelectedTimesheet(row); setShowDetailModal(true); }}
              className="text-slate-300 focus:bg-slate-800 focus:text-white"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
            {row.submission_status === 'Draft' && (
              <>
                <DropdownMenuItem 
                  onClick={() => handleSubmit(row)}
                  className="text-blue-400 focus:bg-blue-500/10"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleDelete(row)}
                  className="text-red-400 focus:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
            {row.submission_status === 'Submitted' && (
              <>
                <DropdownMenuItem 
                  onClick={() => handleApprove(row)}
                  className="text-green-400 focus:bg-green-500/10"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleReject(row)}
                  className="text-red-400 focus:bg-red-500/10"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="timesheets-page">
      <PageHeader
        title="Timesheets"
        description="Track and manage work hours"
        action={() => setShowCreateModal(true)}
        actionLabel="Log Time"
        actionIcon={Clock}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-900/50 border border-slate-800 rounded-sm p-4">
          <p className="text-slate-400 text-sm">Total Entries</p>
          <p className="text-2xl font-bold text-white">{stats.total_entries}</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4">
          <p className="text-blue-400 text-sm">Total Hours</p>
          <p className="text-2xl font-bold text-white">{stats.total_hours}h</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-4">
          <p className="text-green-400 text-sm">Billable Hours</p>
          <p className="text-2xl font-bold text-white">{stats.billable_hours}h</p>
        </div>
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-sm p-4">
          <p className="text-orange-400 text-sm">Overtime</p>
          <p className="text-2xl font-bold text-white">{stats.overtime_hours}h</p>
        </div>
      </div>

      <SearchFilter
        searchValue=""
        onSearchChange={() => {}}
        searchPlaceholder="Search timesheets..."
        filters={[
          {
            key: 'status',
            placeholder: 'Status',
            value: status,
            onChange: setStatus,
            options: [
              { value: 'all', label: 'All Status' },
              { value: 'Draft', label: 'Draft' },
              { value: 'Submitted', label: 'Submitted' },
              { value: 'Approved', label: 'Approved' },
              { value: 'Rejected', label: 'Rejected' },
            ],
          },
        ]}
        onReset={() => setStatus('all')}
      />

      <DataTable
        columns={columns}
        data={timesheets}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        emptyMessage="No timesheet entries found"
      />

      {/* Create Timesheet Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Log Time Entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Date *</Label>
                <Input
                  type="date"
                  value={newEntry.date}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Work Mode</Label>
                <Select value={newEntry.work_mode} onValueChange={(v) => setNewEntry(prev => ({ ...prev, work_mode: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {WORK_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Project</Label>
              <Input
                value={newEntry.project}
                onChange={(e) => setNewEntry(prev => ({ ...prev, project: e.target.value }))}
                placeholder="Project name"
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Link to Task</Label>
              <Select value={newEntry.task_id || "none"} onValueChange={(v) => setNewEntry(prev => ({ ...prev, task_id: v === "none" ? "" : v }))}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Select task (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                  <SelectItem value="none">None</SelectItem>
                  {tasks.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.task_id} - {t.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Check In *</Label>
                <Input
                  type="time"
                  value={newEntry.check_in_time}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, check_in_time: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Check Out *</Label>
                <Input
                  type="time"
                  value={newEntry.check_out_time}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, check_out_time: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Break (min)</Label>
                <Input
                  type="number"
                  value={newEntry.break_minutes}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, break_minutes: parseInt(e.target.value) || 0 }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Work Type</Label>
                <Select value={newEntry.work_type} onValueChange={(v) => setNewEntry(prev => ({ ...prev, work_type: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {WORK_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Activity</Label>
                <Select value={newEntry.activity_category} onValueChange={(v) => setNewEntry(prev => ({ ...prev, activity_category: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {ACTIVITY_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Task Description *</Label>
              <Textarea
                value={newEntry.task_description}
                onChange={(e) => setNewEntry(prev => ({ ...prev, task_description: e.target.value }))}
                placeholder="Describe the work done..."
                className="bg-slate-800 border-slate-700 min-h-[80px]"
                required
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setShowCreateModal(false)} className="text-slate-400">
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="bg-green-500 hover:bg-green-600 text-slate-900">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Log Time
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Timesheet Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-slate-500 font-mono text-sm">{selectedTimesheet?.timesheet_id}</span>
              Timesheet Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Employee</p>
                <p className="text-white">{selectedTimesheet?.employee_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Date</p>
                <p className="text-white">{selectedTimesheet?.date} ({selectedTimesheet?.day})</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Time</p>
                <p className="text-white font-mono">{selectedTimesheet?.check_in_time} - {selectedTimesheet?.check_out_time}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Net Hours</p>
                <p className="text-white font-bold">{selectedTimesheet?.net_hours}h</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Work Type</p>
                <p className="text-white">{selectedTimesheet?.work_type}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Activity</p>
                <p className="text-white">{selectedTimesheet?.activity_category}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Work Mode</p>
                <p className="text-white">{selectedTimesheet?.work_mode}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(selectedTimesheet?.submission_status)}`}>
                  {selectedTimesheet?.submission_status}
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Description</p>
              <p className="text-slate-300 bg-slate-800 p-3 rounded-sm">{selectedTimesheet?.task_description}</p>
            </div>
            {selectedTimesheet?.rejection_reason && (
              <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-sm">
                <p className="text-sm text-red-400 font-medium">Rejection Reason</p>
                <p className="text-slate-300">{selectedTimesheet.rejection_reason}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
